import React, { useEffect, useState } from 'react';

import { NumberFormatValues, OnValueChange, PatternFormatProps } from 'react-number-format';

import { COUNTRY_PHONE_CODES, FORMAT } from '@utils/constants';
import { isValueSet } from '@utils/guards';
import { onlyNumbers } from '@utils/utils';

import { MaskedPatternInput } from '@components/common/StyledInput/MaskedPatternInput';
import { StyledInputProps } from '@components/common/StyledInput/StyledInput';

type RuPhoneInputProps = Omit<
    PatternFormatProps<StyledInputProps>,
    'type' | 'format' | 'onValueChange' | 'onChange'
> & {
    onChange?(value: string): void;
};

export const RuPhoneInput = React.forwardRef<HTMLInputElement, RuPhoneInputProps>((props, ref) => {
    const { value, onChange, ...restProps } = props;

    const [phone, setPhone] = useState(value);

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        const formattedValue = event.clipboardData.getData('Text');
        let fieldValue = onlyNumbers(formattedValue);

        if (fieldValue.length === 0) return;

        if (/^[7|8]/.test(fieldValue)) {
            fieldValue = fieldValue.replace(/^[7|8]/g, COUNTRY_PHONE_CODES.ru);
        }

        if (!fieldValue.startsWith(COUNTRY_PHONE_CODES.ru)) {
            fieldValue = `${COUNTRY_PHONE_CODES.ru}${fieldValue}`;
        }

        setPhone(fieldValue);
    };

    const handleValueChange: OnValueChange = (values: NumberFormatValues) => {
        const { formattedValue, value: clearedValue } = values;
        let fieldValue = formattedValue;

        if (clearedValue.length === 1) {
            fieldValue = `+${COUNTRY_PHONE_CODES.ru} (`;

            if (!/^[7|8]/.test(clearedValue)) {
                fieldValue += clearedValue;
            }
        }

        setPhone(fieldValue);

        if (isValueSet(onChange)) onChange(fieldValue);
    };

    useEffect(() => {
        if (typeof value === 'string' && onlyNumbers(value).length > 1 && value !== phone) {
            setPhone(value);
        }
    }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <MaskedPatternInput
            format={FORMAT.ruPhoneNumber}
            type="tel"
            placeholder="+7"
            isShowPlaceholderOnFocus
            ref={ref}
            value={phone}
            onValueChange={handleValueChange}
            onPaste={handlePaste}
            {...restProps}
        />
    );
});
